import { Component, Element, Event, EventEmitter, h, Host, Listen, Method, Prop, State, Watch } from "@stencil/core";
import { SvgLoader } from "../../common/svg-loader";
import { SvgMetadata } from "../../common/svg-metadata";
import { SvgType } from "../../common/svg-type";
import {
  IconAlignmentsX,
  IconAlignmentsY,
  IconSchemeDefinition,
  IconSchemeName,
  IconSize
} from "./constants/icon-types";
import {
  ICON_ALIGNMENT_X_CENTER,
  ICON_SCHEME_CONFIG,
  ICON_SCHEME_MAP,
  ICONSIZE_NORMAL,
  ICONSIZE_SMALL,
  ICONSIZE_TINY
} from "./constants/icon-constants";
import { applySchemeDelegate, ColorableComponent, setCssVariablesDelegate } from "../../common/colorable-component";
// import { setCssVariable } from "../../utils/css-variables";
import pfx from "../../utils/style-class-prefix-helper";
import { IconSchemeConfiguration } from "../../constants/colors";
import { KeyIds } from "../../constants/keys";
import { TOOLTIP_VISIBILITY_ALWAYS, TOOLTIP_VISIBILITY_ON_CLICK } from "../tooltip/constants/tooltip-constants";
import sanitizeHtml from "sanitize-html";
import { getContextMenuProps } from "../../utils/context-menu-helper";

import { BackgroundColor } from "../background/constants/background-types";
import { setCssVariable } from "../../utils/css-variables";
import { StyledComponent } from "../../decorators/styled-component";
// import { InitLogging } from "../../decorators/init-logging";

/**
 * @slot context-menu - holds eon-ui-context-menu component
 * @slot tooltip - holds eon-ui-tooltip component
 */
@Component({
  tag: "eon-ui-icon",
  styleUrl: "icon.scss",
  shadow: true
})
export class Icon implements ColorableComponent<IconSchemeConfiguration<IconSchemeDefinition>> {
  // @InitLogging() sentryTransaction;
  schemeConfig: IconSchemeConfiguration<IconSchemeDefinition> = ICON_SCHEME_CONFIG;
  schemeMap = ICON_SCHEME_MAP;
  iconWrapper: HTMLElement;
  contextMenu: HTMLEonUiContextMenuElement;
  tooltipElement: HTMLEonUiTooltipElement;

  private interactionObserver?: IntersectionObserver;
  private readonly loader: SvgLoader;
  private svgType: SvgType = SvgType.ICON;

  @StyledComponent() component: any;
  @Element() hostElement!: HTMLEonUiIconElement;

  @State() activeScheme: IconSchemeDefinition;
  @State() private svgContent?: string;
  @State() private svgMetadata?: SvgMetadata;
  @State() private isVisible = false;
  @State() private isVisibilityOnClick: boolean;

  /**
   * The name of the color scheme of the component.
   * If not set, the component will try to calculate it based on the nearest enclosing background information given by inner-background attribute or eon-ui-background component.
   */
  @Prop({ reflect: true }) scheme: IconSchemeName;

  /**
   * Specifies the label to use for accessibility. Defaults to the icon name.
   */
  @Prop({ attribute: "a11y-aria-label", mutable: true, reflect: true }) a11yAriaLabel: string;

  /**
   * Specifies which icon to use from the built-in set of icons.
   */
  @Prop({ reflect: true }) name: string;

  /**
   * Specifies the vertical position of the icon inside the wrapper.
   * Possible Options are top, center, bottom.
   * Default is center.
   */
  @Prop() alignmentY: IconAlignmentsY = ICON_ALIGNMENT_X_CENTER;

  /**
   * Specifies the horizontal position of the icon inside the wrapper.
   * Possible Options are left, center, right.
   * Default is center.
   */
  @Prop() alignmentX: IconAlignmentsX = ICON_ALIGNMENT_X_CENTER;

  /**
   * A boolean that specifies whether or not the icon does have space around inside the wrapper.
   * Default is true.
   */
  @Prop() noSpace: boolean = true;

  /**
   * A boolean that specifies whether or not the icon has the default svg hight.
   * Default is false.
   */
  @Prop() defaultHeight: boolean = false;

  /**
   * The size of the icon.
   */
  @Prop() size: IconSize = ICONSIZE_NORMAL;

  /**
   * If enabled, the icon will be loaded lazily when it's visible in the viewport. Defaults to false.
   */
  @Prop() lazy = false;

  /**
   * Triggered when the image src has been loaded
   */
  @Event() iconSrcLoaded: EventEmitter;

  constructor() {
    this.loader = new SvgLoader();
  }

  componentWillLoad() {
    this.deprecatedFallback();

    // Purposely do not return the promise here because loading the SVG file should not hold up loading the app.
    // Only load the SVG if it's visible.
    this.waitUntilVisible(this.hostElement as HTMLElement, "48px", () => {
      this.isVisible = true;
      this.loadSvgContentAndMetadata();
    });

    this.applyScheme();
    this.initContextMenu();
    this.getTooltipElement();
  }

  componentDidLoad() {
    this.setCssVariables();
  }

  deprecatedFallback() {
    this.a11yAriaLabel = this.hostElement.getAttribute("aria-label") ?? this.a11yAriaLabel;
  }

  disconnectedCallback() {
    if (this.interactionObserver) {
      this.interactionObserver.disconnect();
      this.interactionObserver = undefined;
    }
  }

  /**
   * This method can be called to re-trigger the automatic background color detection
   */
  @Method()
  async updateScheme(backgroundColor?: BackgroundColor) {
    applySchemeDelegate(this, backgroundColor);
  }

  @Watch("scheme")
  applyScheme(): void {
    applySchemeDelegate(this);
  }

  @Watch("activeScheme")
  setCssVariables(): void {
    setCssVariablesDelegate(this);
  }

  @Watch("name")
  onNameChanged() {
    this.loadSvgContentAndMetadata();
  }

  @Watch("size")
  onSizeChanged() {
    this.loadSvgContentAndMetadata();
  }

  /**
   * Normal icons have height="32px" (which is already specified in the svg content) and small icons have height="24px".
   * If this icon is small, the function will update the width property of the svg and set it to the small height.
   *
   * We can't do this in CSS using a ".small" class because the svgContent is set as innerHTML and doesn't get the IE/Edge polyfill class "sc-eon-ui-icon".
   * This means that the transcluded svg can't be accessed using CSS in IE/Edge.
   * @param svgContent the current SVG content
   */
  private loadSvgContentAndMetadata() {
    if (this.isVisible && !!this.name) {
      // Load SVG content
      let loadedSvgContent = this.loader.getSvg(this.name, this.svgType);
      if (loadedSvgContent) {
        this.svgContent = loadedSvgContent;
      } else {
        // async if it hasn't been loaded
        void this.loader
          .requestSvg(this.name, this.svgType)
          .then(() => {
            this.svgContent = this.loader.getSvg(this.name, this.svgType);
          })
          .catch((e) => {
            this.svgContent = null;
            console.error(e.message); // "oh, no!"
          });
      }

      if (this.svgContent) {
        this.iconSrcLoaded.emit();
        // load SVG size
        this.svgMetadata = this.loader.getSvgMetadata(this.name, this.svgType);

        if (!this.svgMetadata) {
          // async if it hasn't been loaded
          void this.loader.requestSvgSizes(this.svgType).then(() => {
            this.svgMetadata = this.loader.getSvgMetadata(this.name, this.svgType);
          });
        }
      }
    }
  }

  /*
   * Gets the svg width and height and make it available as a scss variable
   */
  setSvgWidth() {
    this.svgMetadata && setCssVariable(this.hostElement, "--iconWidth", `${this.svgMetadata.width}`);
  }

  private waitUntilVisible(element: HTMLElement, rootMargin: string, callback: () => void) {
    if (this.lazy && window && window.IntersectionObserver) {
      const io = (this.interactionObserver = new window.IntersectionObserver(
        (data: IntersectionObserverEntry[]) => {
          if (data[0].isIntersecting) {
            io.disconnect();
            this.interactionObserver = undefined;
            callback();
          }
        },
        { rootMargin }
      ));
      io.observe(element);
    } else {
      // browser doesn't support IntersectionObserver, so just show the icon
      callback();
    }
  }

  /**
   * Context Menu Setup including getter, click events, keyboard events and properties
   */
  private initContextMenu() {
    this.contextMenu = this.hostElement.querySelector('[slot="context-menu"]');
  }

  /**
   * Tooltip Check for OnClick visibility property
   */
  private getTooltipElement() {
    this.tooltipElement = this.hostElement.querySelector('[slot="tooltip"]');
    this.isVisibilityOnClick =
      ((this.tooltipElement as unknown) as HTMLEonUiTooltipElement)?.visibility === TOOLTIP_VISIBILITY_ON_CLICK;
  }

  private handleClick(_event: MouseEvent) {
    this.contextMenu && (this.contextMenu.visible = true);
    this.iconWrapper.ariaExpanded = "true";
  }

  private handleKeyDown(e: KeyboardEvent) {
    switch (e.key) {
      case KeyIds.SPACE:
      case KeyIds.ENTER:
        e.preventDefault();
        e.stopPropagation();
        this.contextMenu && (this.contextMenu.visible = true);
        this.tooltipElement && (this.tooltipElement.visible = true);
        break;
      case KeyIds.ESCAPE:
        e.preventDefault();
        this.contextMenu && (this.contextMenu.visible = false);
        this.tooltipElement && (this.tooltipElement.visible = false);
        break;
      default:
        break;
    }
  }

  handleFocusEvent() {
    const tooltipElements = Array.from(document.querySelectorAll("eon-ui-tooltip"));
    tooltipElements.map((item) => {
      item.visibility !== TOOLTIP_VISIBILITY_ALWAYS && (item.visible = false);
    });
  }

  @Listen("click", { target: "window" })
  onDocumentClick(event) {
    if (
      event.target !== this.hostElement &&
      event.target !== this.tooltipElement &&
      event.target.parentElement !== this.tooltipElement
    ) {
      this.tooltipElement &&
        this.tooltipElement.visibility === TOOLTIP_VISIBILITY_ON_CLICK &&
        (this.tooltipElement.visible = false);
    }
  }

  private getTooltipProps() {
    const props = {
      role: "button",
      tabIndex: 0
    };

    return this.contextMenu
      ? props
      : {
          ...props,
          onClick: (event: MouseEvent) => this.handleClick(event),
          onKeyDown: (event: KeyboardEvent) => this.handleKeyDown(event),
          onFocus: () => this.handleFocusEvent()
        };
  }

  render() {
    const sanatizedSvgHtml = sanitizeHtml(this.svgContent, {
      allowedTags: ["svg", "path"],
      allowedAttributes: {
        svg: ["xmlns", "width", "height"],
        path: ["d", "fill-rule", "clip-rule"]
      }
    });

    this.setSvgWidth();

    const classes = pfx({
      "icon-wrapper": true,
      "size-small": this.size === ICONSIZE_SMALL,
      "size-normal": this.size === ICONSIZE_NORMAL,
      "size-tiny": this.size === ICONSIZE_TINY,
      "no-spacing": this.noSpace,
      "default-height": this.defaultHeight,
      "y-top": this.alignmentY === "top",
      "y-center": this.alignmentY === "center",
      "y-bottom": this.alignmentY === "bottom",
      "x-left": this.alignmentX === "left",
      "x-center": this.alignmentX === "center",
      "x-right": this.alignmentX === "right",
      "context-menu": this.contextMenu,
      "tooltip-icon": this.tooltipElement,
      "visibility-on-click": !!this.isVisibilityOnClick,
      hidden: !this.svgContent
    });

    return (
      <Host>
        {this.contextMenu || this.tooltipElement ? (
          <button
            class={classes}
            ref={(el) => (this.iconWrapper = el as HTMLElement)}
            innerHTML={sanatizedSvgHtml}
            onFocus={() => this.handleFocusEvent()}
            onClick={(event: MouseEvent) => this.handleClick(event)}
            onKeyDown={(event: KeyboardEvent) => this.handleKeyDown(event)}
            {...(this.contextMenu &&
              getContextMenuProps(this.hostElement, this.contextMenu.visible, this.a11yAriaLabel))}
            {...(this.tooltipElement && this.getTooltipProps())}
          />
        ) : (
          <div class={classes} ref={(el) => (this.iconWrapper = el as HTMLElement)} innerHTML={sanatizedSvgHtml}></div>
        )}
        <slot name="context-menu" />
        <slot name="tooltip" />
      </Host>
    );
  }
}
